import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectCustomDensity,
  selectCustomSearchDensity,
  setCustomDensity,
} from '../../redux/slices/utils-slice';
import type { DensityType } from '../../utils/density';
import type { TopicListType } from '../../utils/list';
import { isMobile } from '../../utils/web';
import { densityMessages } from './children/DensityItem';
import DensityMenuDesktop from './children/DensityMenuDesktop';
import { DensityMenuMobile } from './children/DensityMenuMobile';

export type CommonDensityMenuProps = {
  options: DensityType[];
  currentDensity: DensityType;
  isCurrentDensity: (density: DensityType) => boolean;
  onDensityChange: (density: DensityType) => void;
  itemsClass?: string;
  isResponsive?: boolean;
};

export const DensityMenu = function DensityMenu({
  densityType: propsDensity = 'compact',
  listType = 'lastTopics',
  isResponsive = true,
}: {
  densityType: DensityType;
  listType?: TopicListType;
  isResponsive?: boolean;
}) {
  const dispatch = useAppDispatch();
  const options = Object.keys(densityMessages) as DensityType[];

  const currentListSelector = useMemo(
    () => (listType === 'lastTopics' ? selectCustomDensity : selectCustomSearchDensity),
    [listType],
  );
  const customDensity = useAppSelector(currentListSelector);
  const currentDensity = propsDensity || customDensity;

  const isCurrentDensity = useCallback(
    (density: DensityType) => density === currentDensity,
    [currentDensity],
  );

  const onDensityChange = useCallback(
    (dType: DensityType) => dispatch(setCustomDensity({ list: listType, density: dType })),
    [dispatch, listType],
  );

  const commonProps = {
    options,
    currentDensity,
    isCurrentDensity,
    onDensityChange,
  };

  return isMobile() || !isResponsive ? (
    <DensityMenuMobile {...commonProps} isResponsive={isResponsive} />
  ) : (
    <DensityMenuDesktop
      className="flex rounded-md h-10 overflow-hidden border border-gray-210 divide-x-[1px] divide-gray-210 self-start"
      itemsClass="flex justify-center w-10 h-10  hover:shadow-inner"
      {...commonProps}
    />
  );
};
